import React from "react";
import { motion } from "framer-motion";
import { Play, ArrowUpRight, Plus, Check } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useCompare } from "../contexts/CompareContext";
import { hasVideo, youtubeThumbnail, videoMap } from "../data/videoMap";

const ACCENT_COLORS = {
  gold: "#D7B56D",
  bronze: "#A9824A",
  cyan: "#76D9E8",
  silver: "#D8DCE2",
  emerald: "#3A7762",
  violet: "#7B6FA8",
  amber: "#D59A4A",
};

const EASE = [0.22, 1, 0.36, 1];

export const AccessCard = ({ product, onOpen, onPlay, large = false, index = 0 }) => {
  const { t, productContent } = useLanguage();
  const { items, toggle } = useCompare();
  const inCompare = items.includes(product.id);
  const accent = ACCENT_COLORS[product.accent] || ACCENT_COLORS.gold;
  const content = productContent(product.id);
  const video = hasVideo(product.id);

  const initials = product.name.split(" ").map((w) => w[0]).join("").slice(0, 3);
  const isFlagship = product.importance === "flagship";

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay: (index % 6) * 0.04, ease: EASE }}
      className={`group relative card-lift rounded-3xl border border-[#D8DCE2]/8 bg-[#15171c] overflow-hidden ${
        large ? "lg:col-span-2" : ""
      }`}
      data-testid={`access-card-${product.id}`}
    >
      {/* Visual top — simplified, less noise */}
      <div
        className={`relative ${large ? "h-[220px]" : "h-[160px]"} overflow-hidden`}
        style={{
          background: `radial-gradient(circle at 30% 20%, ${accent}1f, transparent 55%), linear-gradient(180deg, #1a1d24, #0e1015)`,
        }}
      >
        {/* Minimal geometric line — single curve only */}
        <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 400 220" fill="none" aria-hidden>
          <defs>
            <linearGradient id={`grad-${product.id}`} x1="0" y1="0" x2="400" y2="220">
              <stop offset="0" stopColor={accent} stopOpacity="0.4" />
              <stop offset="1" stopColor={accent} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`M -20 ${130 + (product.rank % 4) * 6} Q 120 ${80 + (product.rank % 5) * 6}, 200 ${110 + (product.rank % 3) * 8} T 420 ${100 + (product.rank % 4) * 8}`}
            stroke={`url(#grad-${product.id})`}
            strokeWidth="1"
            fill="none"
          />
          <circle cx="80" cy="60" r="28" fill="none" stroke={`url(#grad-${product.id})`} strokeWidth="0.8" />
          <circle cx="320" cy="170" r="42" fill="none" stroke={`url(#grad-${product.id})`} strokeWidth="0.8" />
        </svg>

        {/* Top-left badge */}
        <div className="absolute top-4 left-4 flex items-center gap-2.5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-mono text-[11px] font-bold tracking-tight border bg-[#0a0b0f]/40"
            style={{ borderColor: `${accent}55`, color: accent }}
          >
            {initials}
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D8DCE2]/55">
              #{String(product.rank).padStart(2, "0")}
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: accent }}>
              {product.importance}
            </div>
          </div>
        </div>

        {/* Video badge */}
        {video && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlay && onPlay(product.id);
            }}
            className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0a0b0f]/60 backdrop-blur-sm border border-[#76D9E8]/35 hover:border-[#76D9E8] transition-colors duration-200"
            data-testid={`card-video-btn-${product.id}`}
            aria-label="Watch demo"
          >
            <Play size={10} className="text-[#76D9E8]" fill="#76D9E8" />
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#76D9E8]">Demo</span>
          </button>
        )}

        {/* Flagship marker */}
        {isFlagship && (
          <div className="absolute bottom-4 left-4 px-2.5 py-1 rounded-full bg-[#D7B56D] text-[#0a0b0f] font-mono text-[9px] uppercase tracking-[0.22em] font-bold">
            ★ Flagship
          </div>
        )}
      </div>

      {/* Content — more breathing room */}
      <div className="p-7 flex flex-col">
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] mb-3.5" style={{ color: accent }}>
          {product.categoryLabel}
        </div>
        <h3 className="font-display text-[22px] md:text-[24px] font-bold text-[#F5F1EA] tracking-tight leading-tight mb-3">
          {product.name}
        </h3>
        <p className="text-[14px] text-[#D8DCE2]/85 leading-relaxed mb-5 line-clamp-3">
          {content?.short}
        </p>

        <div className="flex flex-wrap gap-1.5 mb-6">
          {product.badges.slice(0, 3).map((b) => (
            <span key={b} className="tag" style={{ borderColor: `${accent}33`, color: accent, background: `${accent}08` }}>
              {b}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6 pt-5 border-t border-[#D8DCE2]/8">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A9824A] mb-1.5">{t("cardLevel")}</div>
            <div className="text-[13px] text-[#E6E0D6]">{product.level.join(", ")}</div>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A9824A] mb-1.5">{t("cardEnvironment")}</div>
            <div className="text-[13px] text-[#E6E0D6] truncate">{product.environment}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-auto">
          <button
            onClick={() => onOpen(product.id)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#F5F1EA] text-[#0a0b0f] font-bold text-[12px] tracking-tight hover:bg-[#D7B56D] transition-colors duration-200"
            data-testid={`card-open-${product.id}`}
          >
            {t("cardOpenDetails")} <ArrowUpRight size={13} />
          </button>
          <button
            onClick={() => toggle(product.id)}
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 border ${
              inCompare
                ? "bg-[#D7B56D] border-[#D7B56D] text-[#0a0b0f]"
                : "border-[#D8DCE2]/15 text-[#D8DCE2] hover:border-[#D7B56D]/45"
            }`}
            data-testid={`card-compare-${product.id}`}
            aria-label={inCompare ? t("cardRemove") : t("cardAddToCompare")}
          >
            {inCompare ? <Check size={14} /> : <Plus size={14} />}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
